// src/auth/pendingSignIn.ts
//
// Remembers, for this TAB, that a sign-in is waiting on a two-factor code — so a
// refresh returns to the code step instead of throwing the person back to email
// and password.
//
// WHY THIS IS SAFE TO KEEP CLIENT-SIDE: nothing here is a credential. The thing
// the server actually checks is Better Auth's signed two-factor challenge cookie,
// which already survives a refresh and lives for TWO_FACTOR_CHALLENGE_MS. This
// only remembers which screen to show and whose address to display. If the
// cookie has gone, verify-totp answers INVALID_TWO_FACTOR_COOKIE and the page
// falls back to the password step — this state can never let anyone past the
// second factor.
//
// sessionStorage, not localStorage: it dies with the tab, so a half-finished
// sign-in on a shared office PC is not waiting for the next person who opens the
// portal tomorrow.

/**
 * Must match the twoFactor plugin's challenge-cookie lifetime on the server
 * (`twoFactorCookieMaxAge`, Better Auth's default of 600 seconds). If the server
 * value is ever changed, change this with it — a longer value here shows a code
 * screen the server will already refuse.
 */
export const TWO_FACTOR_CHALLENGE_MS = 10 * 60 * 1000;

/** Set just before redirecting after a successful sign-in; read once by PasskeyNudge. */
export const JUST_SIGNED_IN_KEY = "cits.justSignedIn";

export interface PendingSignIn {
  email: string;
  startedAt: number;
}

export type PendingState =
  | { status: "none" }
  | { status: "active"; pending: PendingSignIn }
  | { status: "expired"; email: string };

/** Build a namespaced key so the tenant and operator portals never read each other's state. */
function keyFor(scope: string) {
  return `cits.${scope}.pendingSignIn`;
}

export function savePending(scope: string, email: string, now = Date.now()): void {
  try {
    sessionStorage.setItem(keyFor(scope), JSON.stringify({ email, startedAt: now }));
  } catch {
    // Private mode / blocked storage: the refresh-resume is a convenience, and
    // the sign-in itself must not fail because we could not remember a screen.
  }
}

export function clearPending(scope: string): void {
  try {
    sessionStorage.removeItem(keyFor(scope));
  } catch {
    /* see savePending */
  }
}

/** What the page should show on load. Expired state is cleared as it is read. */
export function readPending(scope: string, now = Date.now()): PendingState {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(keyFor(scope));
  } catch {
    return { status: "none" };
  }
  if (!raw) return { status: "none" };

  let parsed: PendingSignIn;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearPending(scope);
    return { status: "none" };
  }
  if (!parsed?.email || typeof parsed.startedAt !== "number") {
    clearPending(scope);
    return { status: "none" };
  }
  if (now - parsed.startedAt >= TWO_FACTOR_CHALLENGE_MS) {
    clearPending(scope);
    return { status: "expired", email: parsed.email };
  }
  return { status: "active", pending: parsed };
}

/** Milliseconds left on the challenge, never negative. */
export function remainingMs(pending: PendingSignIn, now = Date.now()): number {
  return Math.max(0, pending.startedAt + TWO_FACTOR_CHALLENGE_MS - now);
}

/** "9:41" */
export function formatRemaining(ms: number): string {
  const total = Math.ceil(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** Better Auth's answer when the challenge cookie has expired or is missing. */
export function isChallengeExpired(error: { code?: string; message?: string } | null | undefined): boolean {
  return error?.code === "INVALID_TWO_FACTOR_COOKIE" || /two factor cookie/i.test(error?.message ?? "");
}

export function markJustSignedIn(): void {
  try {
    sessionStorage.setItem(JUST_SIGNED_IN_KEY, "1");
  } catch {
    /* the nudge simply will not show */
  }
}

/** Returns true once, then forgets — so the nudge appears only right after sign-in. */
export function consumeJustSignedIn(): boolean {
  try {
    const was = sessionStorage.getItem(JUST_SIGNED_IN_KEY) === "1";
    sessionStorage.removeItem(JUST_SIGNED_IN_KEY);
    return was;
  } catch {
    return false;
  }
}
